import React, { useRef, useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Pagination from "@material-ui/lab/Pagination";
import theme from "../../../../assets/theme";
import COLORS from "../../../../assets/Colors";
import styled from "styled-components";

const useStyles = makeStyles(() => ({
  root: {
    "& .MuiPaginationItem-root": {
      margin: 0,
      fontFamily: theme.fonts.primaryFontRegular,
      color: COLORS.COLOR_DARK,
      fontSize: 13,
      height: 34,
      width: 34,
      borderRadius: 8,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      transition: "background 0.15s ease, color 0.15s ease",
    },
    "& .MuiPaginationItem-outlined": {
      border: 0,
    },
    "& .MuiPaginationItem-page:hover": {
      backgroundColor: "rgba(41, 52, 97, 0.06)",
    },
    "& .MuiPaginationItem-page.Mui-selected": {
      backgroundColor: "#293461",
      color: "#fff",
      borderRadius: 8,
      fontFamily: theme.fonts.primaryFontBold,
      boxShadow: "0 2px 8px rgba(41, 52, 97, 0.28)",
    },
    "& .MuiPaginationItem-page.Mui-selected:hover": {
      backgroundColor: "#293461",
    },
    "& .MuiPagination-ul": {
      display: "flex",
      alignItems: "center",
      gap: 2,
    },
    "& li": {
      borderRight: 0,
    },
    "& .MuiPaginationItem-ellipsis": {
      height: 34,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "#acb1c2",
    },
  },
}));

export default function CustomPagination({ MTRef, state, selectedFilter }) {
  const classes = useStyles();
  const [page, setPage] = useState(1);
  const [jumpValue, setJumpValue] = useState("");
  const PaginationRef = useRef();

  const lastPage = state?.lastPage || 1;

  useEffect(() => {
    setPage(1);
  }, [selectedFilter]);

  useEffect(() => {
    if (page > lastPage) {
      setPage(1);
      if (MTRef?.current?.onChangePage) {
        MTRef.current.onChangePage(null, 0);
      }
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastPage]);

  function handlePageChange(newPage) {
    const safePage = Math.max(1, Math.min(newPage, lastPage));
    MTRef.current.onChangePage(null, safePage - 1);
    setPage(safePage);
  }

  function handleJumpGo() {
    const val = parseInt(jumpValue, 10);
    if (!isNaN(val) && val >= 1 && val <= lastPage) {
      handlePageChange(val);
      setJumpValue("");
    }
  }

  const clampedPage = Math.min(page, lastPage);

  return (
    <PaginationWrapper>
      <PageInfo>
        Page <strong>{clampedPage}</strong> of <strong>{lastPage}</strong>
      </PageInfo>

      <Controls>
        {/* First */}
        <NavBtn
          onClick={() => handlePageChange(1)}
          disabled={clampedPage === 1}
          title="First page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="11 17 6 12 11 7" />
            <polyline points="18 17 13 12 18 7" />
          </svg>
        </NavBtn>

        {/* Prev */}
        <NavBtn
          onClick={() => handlePageChange(clampedPage - 1)}
          disabled={clampedPage === 1}
          title="Previous page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </NavBtn>

        {/* MUI page numbers */}
        <div className={classes.root}>
          <Pagination
            ref={PaginationRef}
            count={lastPage}
            onChange={(e, p) => {
              MTRef.current.onChangePage(e, p - 1);
              setPage(p);
            }}
            variant="outlined"
            shape="rounded"
            page={clampedPage}
            hidePrevButton
            hideNextButton
            siblingCount={1}
            boundaryCount={1}
          />
        </div>

        {/* Next */}
        <NavBtn
          onClick={() => handlePageChange(clampedPage + 1)}
          disabled={clampedPage >= lastPage}
          title="Next page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </NavBtn>

        {/* Last */}
        <NavBtn
          onClick={() => handlePageChange(lastPage)}
          disabled={clampedPage >= lastPage}
          title="Last page"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="13 17 18 12 13 7" />
            <polyline points="6 17 11 12 6 7" />
          </svg>
        </NavBtn>
      </Controls>

      {/* Jump to page — fully controlled */}
      <JumpWrap>
        <JumpLabel>Go to</JumpLabel>
        <JumpInput
          type="number"
          min={1}
          max={lastPage}
          value={jumpValue}
          placeholder="—"
          onChange={(e) => setJumpValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleJumpGo();
          }}
        />
        <GoBtn
          onClick={handleJumpGo}
          disabled={
            jumpValue === "" ||
            isNaN(parseInt(jumpValue, 10)) ||
            parseInt(jumpValue, 10) < 1 ||
            parseInt(jumpValue, 10) > lastPage
          }
        >
          Go
        </GoBtn>
      </JumpWrap>
    </PaginationWrapper>
  );
}

const PaginationWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
  flex-wrap: wrap;

  @media (max-width: 600px) {
    justify-content: center;
    gap: 8px;
  }
`;

const PageInfo = styled.div`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 13px;
  color: #acb1c2;
  white-space: nowrap;
  padding-right: 4px;

  strong {
    font-family: ${theme.fonts.primaryFontBold};
    color: ${COLORS.COLOR_DARK};
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  background: #f5f6fa;
  border: 1.5px solid #e1e3ee;
  border-radius: 10px;
  padding: 3px;
`;

const NavBtn = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 7px;
  border: none;
  background: none;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ disabled }) => (disabled ? "#d0d3de" : COLORS.COLOR_DARK)};
  transition:
    background 0.15s ease,
    color 0.15s ease,
    transform 0.1s ease;
  outline: none;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    background: rgba(41, 52, 97, 0.08);
    transform: scale(1.05);
  }
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
  &:focus-visible {
    box-shadow: 0 0 0 2px rgba(41, 52, 97, 0.3);
  }
`;

const JumpWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;

  @media (max-width: 480px) {
    display: none;
  }
`;

const JumpLabel = styled.span`
  font-family: ${theme.fonts.primaryFontRegular};
  font-size: 12px;
  color: #acb1c2;
  white-space: nowrap;
`;

const JumpInput = styled.input`
  width: 48px;
  height: 34px;
  border-radius: 8px;
  border: 1.5px solid #e1e3ee;
  background: #fff;
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 13px;
  color: ${COLORS.COLOR_DARK};
  text-align: center;
  outline: none;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease;

  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &::placeholder {
    color: #d0d3de;
    font-family: ${theme.fonts.primaryFontRegular};
  }

  &:focus {
    border-color: #293461;
    box-shadow: 0 0 0 3px rgba(41, 52, 97, 0.1);
  }
`;

const GoBtn = styled.button`
  height: 34px;
  padding: 0 11px;
  border-radius: 8px;
  border: 1.5px solid ${({ disabled }) => (disabled ? "#e1e3ee" : "#293461")};
  background: ${({ disabled }) => (disabled ? "#f5f6fa" : "#293461")};
  color: ${({ disabled }) => (disabled ? "#acb1c2" : "#fff")};
  font-family: ${theme.fonts.primaryFontBold};
  font-size: 12px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  transition:
    background 0.15s ease,
    border-color 0.15s ease,
    color 0.15s ease,
    transform 0.1s ease;
  outline: none;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: #1e2a50;
    transform: translateY(-1px);
  }
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(41, 52, 97, 0.25);
  }
`;
